import { useInfiniteQuery } from '@tanstack/react-query';

import { useQueryKeys } from '@/shared/hooks/use-query-keys';
import {
  listCreatorDiscoverySearchResults,
  type ListCreatorDiscoverySearchResultsRequest,
  type SearchResultListResponse,
} from '../api';
import type { Creator } from '../schema';

type SearchResultsRequest = Omit<ListCreatorDiscoverySearchResultsRequest, 'attempt'> & {
  required_post_features?: string[];
};

type UseDiscoverySearchResultsOptions = {
  enabled?: boolean;
  initialAttempt?: number;
};

export function useDiscoverySearchResults(
  discoverySessionId: string | null | undefined,
  searchId: string | null | undefined,
  request: SearchResultsRequest = {},
  options?: UseDiscoverySearchResultsOptions,
) {
  const queryKeys = useQueryKeys();
  const sessionId = discoverySessionId ?? '';
  const resolvedSearchId = searchId ?? '';
  const initialAttempt = options?.initialAttempt ?? 1;
  const queryRequest = { show_saved: request.show_saved };

  const query = useInfiniteQuery({
    enabled: sessionId.length > 0 && resolvedSearchId.length > 0 && (options?.enabled ?? true),
    queryKey: queryKeys.discovery.searchResults(sessionId, resolvedSearchId, queryRequest),
    queryFn: ({ pageParam, signal }) =>
      listCreatorDiscoverySearchResults(
        sessionId,
        resolvedSearchId,
        {
          show_saved: request.show_saved,
          attempt: pageParam,
        },
        signal,
      ),
    initialPageParam: initialAttempt,
    getNextPageParam: (lastPage, allPages) =>
      getNextDiscoverySearchResultsPageParam(lastPage, allPages, initialAttempt),
  });

  const allCreators = query.data?.pages.flatMap((page) => page.creators) ?? [];
  const flatCreators = filterDiscoveryCreatorsByRequiredFeatures(
    allCreators,
    request.required_post_features,
  );

  const isComplete = !query.hasNextPage && query.isSuccess;
  const attemptCount = query.data?.pages.length ?? 0;

  const stats = {
    total: query.data?.pages.at(-1)?.total_count || 0,
    fullMatched: flatCreators.length,
  };

  return {
    ...query,
    creators: flatCreators || [],
    attemptCount,
    isComplete,
    stats,
  };
}

export function filterDiscoveryCreatorsByRequiredFeatures(
  creators: Creator[],
  requiredFeatures: string[] | null | undefined,
) {
  const normalizedRequiredFeatures = normalizeFeatureSet(requiredFeatures);

  if (normalizedRequiredFeatures.size === 0) return creators;

  return creators.filter((creator) => {
    const normalizedMatchedFeatures = normalizeFeatureSet(creator.matched_keywords);

    return Array.from(normalizedRequiredFeatures).every((feature) =>
      normalizedMatchedFeatures.has(feature),
    );
  });
}

function normalizeFeatureSet(features: string[] | null | undefined) {
  return new Set(
    (features ?? [])
      .map((feature) => feature.trim().toLowerCase())
      .filter((feature) => feature.length > 0),
  );
}

export function getNextDiscoverySearchResultsPageParam(
  lastPage: SearchResultListResponse,
  allPages: SearchResultListResponse[],
  initialAttempt = 1,
) {
  const loadedCount = allPages.reduce((sum, page) => sum + page.creators.length, 0);
  const isTerminal = lastPage.complete === true && loadedCount >= lastPage.total_count;

  if (isTerminal) return undefined;

  return initialAttempt + allPages.length;
}
